'use client';

import { Box, Card, CardActionArea, CardContent, Link, Typography, useTheme } from '@mui/material';
import { IconArrowRight, IconCalculator } from '@tabler/icons-react';
import React, { Fragment, ReactNode } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { ParsePillHeading } from '@/components/ParsePillHeading';
import { StrapiImage } from '@/components/Utility/StrapiImage';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { CustomShadows } from '@/theme';
import { ComponentCalculatorCalculatorHighlight, LoanCalculator } from '@/types';

import { CustomBackground } from '../CustomBackground/CustomBackground';

type CalculatorHighlightsProps = {
  CalculatorHighlights?: ComponentCalculatorCalculatorHighlight | null;
};

export default function CalculatorHighlights({ CalculatorHighlights }: CalculatorHighlightsProps) {
  const theme = useTheme();
  if (!CalculatorHighlights) return null;

  const { HightlightCalculator: highlight, LoanCalculator: calculators } = CalculatorHighlights;

  if (!calculators || calculators.length === 0) return null;

  const otherCalculators: LoanCalculator[] = [];

  const seenTitles = new Set<string>();

  const highlightTitle =
    CalculatorHighlights?.HightlightCalculator?.CalculatorTitle?.trim()?.toLowerCase() ||
    CalculatorHighlights?.CalcHighlightTitle?.trim()?.toLowerCase() ||
    '';

  (calculators || []).forEach((calc) => {
    if (!calc) return;

    const title = calc.CalculatorTitle?.trim()?.toLowerCase() || '';

    if (highlightTitle && title === highlightTitle) return;

    if (seenTitles.has(title)) return;

    seenTitles.add(title);
    otherCalculators.push(calc);
  });

  const renderCalculator = (calc: LoanCalculator, highlight: boolean = false) => (
    <Card
      key={calc?.documentId}
      sx={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 6,
        background: highlight
          ? theme.palette.gradients.purpleLight
          : theme.palette.gradients.purpleWhite,
        boxShadow: (theme.shadows as CustomShadows).a,
        p: 1,
        transition: 'all 0.25s ease',
        minWidth: {
          xs: `calc(100% - calc(${theme.spacing(2)} * 2))`,
          sm: `calc(50% - calc(${theme.spacing(2.75)} * 2))`,
          md: `calc(33% - calc(${theme.spacing(2.85)} * 2))`,
        },
        maxWidth: {
          xs: `calc(100% - calc(${theme.spacing(2)} * 2))`,
          sm: `calc(50% - calc(${theme.spacing(2.75)} * 2))`,
          md: `calc(33% - calc(${theme.spacing(2.85)} * 2))`,
        },
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: (theme.shadows as CustomShadows).b,
        },
      }}
    >
      <CardActionArea
        component="a"
        href={calc?.CalculatorPage?.Slug ? `/${calc.CalculatorPage.Slug}` : '#'}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          p: 2,
          '&:hover': {
            background: 'transparent',
          },
        }}
      >
        <CardContent
          sx={{
            width: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            color: theme.palette.text.primary,
            p: 0,
            position: 'relative',
          }}
        >
          {/* Title + Icon */}
          <Box sx={{ mb: 0.25 }}>
            <Box
              sx={{
                width: '2.8rem',
                height: '2.8rem',
                borderRadius: '50%',
                backgroundColor: theme.palette.purple[200],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {calc?.CalculatorCategory?.CategoryIcon ? (
                <Box
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    alignSelf: 'center',
                    justifySelf: 'center',
                    textAlign: 'center',
                    width: '1.8rem',
                    height: '1.8rem',
                  }}
                >
                  <StrapiImage
                    image={calc?.CalculatorCategory?.CategoryIcon}
                    size="large"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      borderRadius: '12px',
                    }}
                    role="presentation"
                  />
                </Box>
              ) : (
                <IconCalculator
                  size="1.8rem"
                  stroke={1.5}
                  color={theme.palette.purple[700]}
                  role="presentation"
                />
              )}
            </Box>
            <Typography
              variant="h3"
              fontWeight={700}
              sx={{
                display: 'inline-block',
                py: 1,
                fontSize: theme.typography.h5.fontSize,
                color: theme.palette.purple[900],
                flexGrow: 1,
              }}
            >
              {calc?.CalculatorTitle || calc?.CalculatorType}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            {Array.isArray(calc?.CalculatorDescription) && (
              <Box
                sx={{
                  '*': {
                    fontSize: theme.typography.small.fontSize,
                    lineHeight: theme.typography.small.lineHeight,
                  },
                  '& p': {
                    pb: 0,
                    mb: 0.5,
                  },
                  '& ul': {
                    fontSize: theme.typography.small.fontSize,
                    mt: 0.5,
                    mb: 0.5,
                    pl: 1.5,
                    '& > li': {
                      '*': {
                        fontSize: theme.typography.small.fontSize,
                        lineHeight: theme.typography.small.lineHeight,
                      },
                      marginBottom: 0,
                      pl: 0,
                      ml: 1,
                    },
                  },
                }}
              >
                <GlobalBlockRenderer
                  content={calc.CalculatorDescription || []}
                  overrides={{
                    blocks: {
                      heading: ({ children }: { children: ReactNode }) => (
                        <Typography variant="h6" sx={{ pb: 2, textAlign: 'left' }}>
                          {children}
                        </Typography>
                      ),
                      paragraph: ({ children }: { children: ReactNode }) => (
                        <Typography
                          variant="body2"
                          sx={{
                            pb: 1.5,
                            fontSize: '1rem',
                            lineHeight: 1.6,
                          }}
                        >
                          {children}
                        </Typography>
                      ),
                      list: ({ children, format }: { children?: ReactNode; format: string }) => (
                        <Box
                          component={format === 'ordered' ? 'ol' : 'ul'}
                          sx={{
                            pl: 2.5,
                            mt: 1,
                            '& li': {
                              fontSize: '1em',
                              lineHeight: 1.6,
                              mb: 0.5,
                            },
                            '& li span, & li p': {
                              fontSize: '1rem',
                              lineHeight: 1.6,
                            },
                          }}
                        >
                          {children}
                        </Box>
                      ),
                    },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Pinned Button */}
          {calc?.CalculatorPage?.Slug && (
            <Link
              color="primary"
              href={`/${calc.CalculatorPage?.Slug}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                fontSize: theme.typography.small.fontSize,
                mt: 1.5,
                mb: -0.5,
                borderRadius: 2,
                textTransform: 'none',
                alignSelf: 'flex-start',
                fontWeight: 600,
                textDecoration: 'none',
                transition: 'text-decoration 0.25s ease, color 0.25s ease',

                '[class*="MuiCard-root"]:hover &, [class*="MuiCard-root"]:focus &, &:hover, &:focus':
                  {
                    textDecoration: 'underline',
                  },
              }}
            >
              {(calc?.CustomCalculatorCTA ?? calc?.CalculatorTitle)
                ? `Open ${calc?.CalculatorTitle}`
                : 'Open Calculator'}
              <IconArrowRight size="1.2em" />
            </Link>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );

  return (
    <CustomBackground
      displayOptions={
        {
          ...CalculatorHighlights.CalculatorHighlightDisplayOptions,
          PaddingTop: CalculatorHighlights.CalculatorHighlightDisplayOptions?.PaddingTop ?? 'md',
          PaddingBottom:
            CalculatorHighlights.CalculatorHighlightDisplayOptions?.PaddingBottom ?? 'md',
          PaddingInline:
            CalculatorHighlights.CalculatorHighlightDisplayOptions?.PaddingInline ?? 'md',
          ContentsWidth:
            CalculatorHighlights.CalculatorHighlightDisplayOptions?.ContentsWidth ?? 'Wide',
        } as StrapiDisplayOptions
      }
      backgroundOptions={
        {
          ...CalculatorHighlights.CalculatorHighlightBackground,
          BackgroundColor:
            CalculatorHighlights.CalculatorHighlightBackground?.BackgroundColor ?? 'none',
        } as StrapiBackgroundOptions
      }
    >
      {!CalculatorHighlights?.CalcHighlightBlurb && (
        <ParsePillHeading
          title={CalculatorHighlights?.CalcHighlightTitle || 'Our Top _Calculators_'}
        />
      )}
      {CalculatorHighlights?.CalcHighlightBlurb && (
        <GlobalBlockRenderer content={CalculatorHighlights?.CalcHighlightBlurb || []} />
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          rowGap: 3,
          columnGap: 3,
          py: 2,
          px: 2,
          pb: 3,
        }}
      >
        {highlight && renderCalculator(highlight, true)}
        {otherCalculators.map((calc) => (
          <Fragment key={`calc${calc.CalculatorPage?.Slug}`}>
            {renderCalculator(calc, false)}
          </Fragment>
        ))}
      </Box>
    </CustomBackground>
  );
}
